import { useEffect, useRef, useState } from 'react';
import { useServices } from '../services/context.js';
import type { Callables } from '../services/types.js';

type Decision = Awaited<ReturnType<Callables['manageMediaReview']>>;
/** One selected candidate, not a second asset library. Server authorizes every
 * request; unmount on target change prevents stale reads crossing creations. */
export function MediaReviewPanel({ creationId, variantId, onChanged, onClose, onSelect, audioOnly = false }: {
  creationId: string; variantId: string; onChanged: () => void; onClose: () => void; onSelect?: (id: string) => void;
  audioOnly?: boolean;
}) {
  const services = useServices();
  const [decision, setDecision] = useState<Decision | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [confirmReject, setConfirmReject] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    let active = true;
    services.callable.manageMediaReview({ creationId, variantId, action: variantId ? 'status' : audioOnly ? 'audio-list' : 'list' })
      .then(value => { if (active) setDecision(value); })
      .catch(err => { if (active) setError(err instanceof Error ? err.message : 'Could not load media decision'); })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; mounted.current = false; };
  }, [services, creationId, variantId, audioOnly]);
  async function act(action: 'reject' | 'undo') {
    if (!decision || busy) return;
    if (action === 'reject' && !confirmReject) { setConfirmReject(true); return; }
    setBusy(true); setError(''); setMessage('');
    try {
      const value = await services.callable.manageMediaReview({ creationId, variantId, action,
        ...(action === 'undo' && decision.rejectedAtMs !== null ? { rejectedAtMs: decision.rejectedAtMs } : {}) });
      if (!mounted.current) return;
      setDecision(value);
      setConfirmReject(false);
      setMessage(action === 'reject'
        ? 'Rejected for managed asset reuse. Upload or choose replacement media under the same name, then publish it.'
        : 'Rejection undone. Nothing was reattached; choose an asset explicitly.');
      onChanged();
    } catch (err) {
      if (mounted.current) setError(err instanceof Error ? err.message : 'Media decision failed');
    } finally { if (mounted.current) setBusy(false); }
  }
  return <section className="panel" aria-label="Exact-byte media review">
    <h3>Reuse decision</h3>
    <p>Skip means “not now.” Reject blocks these exact managed media bytes for this game, including renamed copies. Similar-looking artwork is not blocked.</p>
    <p>To steer future candidates away from a look or a sound, edit <strong>Avoid this direction</strong> on the composer&apos;s Generate step — that is a preference the next generations read, not a block.</p>
    <p>Published games and demos do not change here. Older versions retain their media. This decision covers managed media, not arbitrary source-embedded URLs.</p>
    {error && <p role="alert">{error}</p>}
    {message && <p role="status">{message}</p>}
    {!variantId && decision && <div>
      {(decision.rejections ?? []).length === 0 && <p>No active rejections.</p>}
      {decision.rejections?.map(item => <button key={item.variantId} className="btn" onClick={() => onSelect?.(item.variantId)}>
        Review rejected {item.name}
      </button>)}
    </div>}
    {variantId && decision && <p>{decision.name}: {decision.rejected ? 'Rejected' : 'Not rejected'}</p>}
    {confirmReject && <div role="group" aria-label="Confirm exact-byte rejection">
      <p>Reject these exact bytes for “{decision?.name}”? Matching working-copy attachments will be removed. Published versions stay unchanged.</p>
      <button className="btn" disabled={busy} onClick={() => void act('reject')}>Confirm rejection</button>
      <button className="btn btn--ghost" disabled={busy} onClick={() => setConfirmReject(false)}>Keep asset</button>
    </div>}
    {variantId && decision && !confirmReject && <button className="btn" disabled={busy} onClick={() => void act(decision.rejected ? 'undo' : 'reject')}>
      {busy ? 'Saving…' : decision.rejected ? 'Undo rejection' : 'Reject exact bytes'}
    </button>}
    <button className="btn btn--ghost" disabled={busy} onClick={onClose}>Close</button>
  </section>;
}
